import { ActionIcon, Divider, Flex, Grid, Text, Tooltip } from "@mantine/core";
import { IconCopy, IconListTree } from "@tabler/icons-react";
import classNames from "classnames";
import { useState } from "react";
import { CheckListType } from "src/components/pages/Settings/Miscellaneous/CheckLists/CheckLists.types";
import { StyledGridCol } from "src/components/pages/Summary/VerifyEntries/VerifyEntries.styled";
import { getCheckListProgress } from "src/generator/utils/getCheckListProgress";
import {
  type ICheckList,
  type ICheckListItem,
  type SetChecklist,
} from "src/interfaces";
import { useDispatch } from "src/redux/hooks";
import { setEditing } from "src/redux/slices/entrySlice";
import { setCheckList as setStateChecklist } from "src/redux/slices/generatorSlice";
import { Notification } from "src/utils/Notification";
import { v4 as uuidv4 } from "uuid";

import styles from "./ChecklistListRow.module.scss";
import { formatChecklistSuffix } from "./checklists.utils";

interface Props {
  row: ICheckList;
  setCheckList: SetChecklist;
  disableCopy: boolean;
  disabled: boolean;
}

const ChecklistListRow = ({
  row,
  setCheckList,
  disableCopy,
  disabled,
}: Props) => {
  const dispatch = useDispatch();
  const [itemsVisible, setItemsVisible] = useState(false);
  const handleCopy = async (e: any) => {
    e.stopPropagation();
    if (disableCopy || disabled) return;
    const { sort, data, suffix } = row;

    const itemMapper = (el: ICheckListItem) => ({
      text: el.text,
      unit: el.unit,
      naAvailable: el.naAvailable ?? false,
    });

    let clearedItems = [];
    if (data.type === CheckListType.Single)
      clearedItems = data.list.map(itemMapper);
    else {
      clearedItems = data.list.map((sub: any) => {
        const items = sub.items?.map(itemMapper);
        return { ...sub, items };
      });
    }

    dispatch(
      setStateChecklist({
        ...row,
        data: { ...data, list: clearedItems },
        suffix: suffix + 1,
        sort: sort + 1,
        cid: uuidv4(),
      }),
    );

    dispatch(setEditing(false));
    setTimeout(() => dispatch(setEditing(true)), 100);
    Notification("Success", "Check List has been copied successfully", "green");
  };

  const handleClick = () => {
    if (disabled) return;
    if (row.data.type === CheckListType.Group) setItemsVisible(!itemsVisible);
    else setCheckList({ ...row, selectedItemIndex: null });
  };

  return (
    <Grid
      onClick={handleClick}
      className={classNames(styles.row, disabled && styles.rowDisabled)}>
      <StyledGridCol justify="" align="center" span={8}>
        {row.name}
        {formatChecklistSuffix(row.suffix)}
      </StyledGridCol>

      <StyledGridCol justify="" align="center" span={1}>
        {row.version}
      </StyledGridCol>
      <StyledGridCol justify="" align="center" span={2}>
        <Flex w="100%" justify="end" align="center" gap="sm">
          <Text size="sm">
            {row.data.type !== CheckListType.Group &&
              getCheckListProgress(row.data.list)}
          </Text>
          {row.data.type === CheckListType.Group && (
            <Tooltip label="Show group items" position="bottom" withArrow>
              <Flex align="center">
                <Text size="sm">
                  ({row.data.list.filter(el => !el.hidden).length})
                </Text>
                <ActionIcon
                  onClick={e => {
                    e.stopPropagation();
                    setItemsVisible(!itemsVisible);
                  }}>
                  <IconListTree />
                </ActionIcon>
              </Flex>
            </Tooltip>
          )}
          {!window.api && (
            <Tooltip
              label="Copy"
              position="bottom"
              withArrow
              disabled={disabled}>
              <ActionIcon
                onClick={handleCopy}
                style={{
                  display: disableCopy ? "none" : "block",
                  cursor: disabled ? "not-allowed" : "pointer",
                }}>
                <IconCopy />
              </ActionIcon>
            </Tooltip>
          )}
        </Flex>
      </StyledGridCol>
      {row.data.type === CheckListType.Group && itemsVisible && (
        <>
          <Grid.Col
            span={12}
            style={{
              padding: "0px 20px",
              boxSizing: "border-box",
            }}>
            <Divider my="sm" label="Items" size="sm" />
          </Grid.Col>
          {row.data.list.map(
            (sub, i) =>
              !sub.hidden && (
                <Grid.Col
                  span={12}
                  className={styles.nestedCol}
                  key={`checklist-item-${i}`}
                  onClick={() => {
                    setCheckList({ ...row, selectedItemIndex: i });
                  }}>
                  <Grid className={styles.nestedRow}>
                    <Grid.Col span={10} className={styles.nestedCell}>
                      <p className={styles.nestedText}>
                        {sub?.name + (sub?.desc ? " - " + sub?.desc : "")}
                      </p>
                    </Grid.Col>
                    <Grid.Col span={2} className={styles.nestedCellRight}>
                      <Flex align="center">
                        <p className={styles.nestedTextLimited}>
                          {getCheckListProgress(
                            (sub as unknown as { items: any[] }).items,
                          )}
                        </p>
                      </Flex>
                    </Grid.Col>
                  </Grid>
                </Grid.Col>
              ),
          )}
        </>
      )}
    </Grid>
  );
};

export default ChecklistListRow;
